import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Checkbox, Spin, DatePicker } from "antd";
import dayjs from "dayjs";
import { addUser } from "../../../service/GroupService";
import { useAddableUsers, useEnrolledItems } from "../../../hooks/GroupHooks/useGetUsers";
import PageLoader from "../../../components/global/Loader/PageLoader";
import CustomModal from "../../../components/global/CustomModal/CustomModal";
import CustomButton from "../../../components/global/Button/CustomButton";
import InputWrapper from "../../../components/shared/Input/Input-Wrapper";
import CustomLabelDropdown from "../../../components/global/CustomLabelDropdown/CustomLabelDropdown";
import { showSuccessMessage, showErrorMessage } from "../../../components/shared/pagination/messages";
import { PayloadBuilder } from "../../../utility/payloadBuilder";

const AddUserModal = ({
  isModalOpen,
  getUsers,
  getCourses,
  getBundles,
  groupId,
  handleCloseModal,
}) => {
  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedEmployees = Form.useWatch("employees", form) || [];
  const selectedCourses = Form.useWatch("courses", form) || [];
  const selectedBundles = Form.useWatch("bundles", form) || [];
  const { userList, fetchAddableUsers, isLoading: usersLoading } = useAddableUsers(groupId);
  const {
    enrolledCourses,
    enrolledBundles,
    isLoading: enrolledItemsLoading,
    fetchEnrolledItems
  } = useEnrolledItems(groupId);

  const filteredUsers = useMemo(
    () => userList?.filter((user) => user.label.toLowerCase().includes(searchValue.toLowerCase())),
    [userList, searchValue]
  );

  const filteredCourses = enrolledCourses.filter((course) =>
    course.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const filteredBundles = enrolledBundles.filter((bundle) =>
    bundle.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const allUserValues = filteredUsers?.map((user) => user.value) || [];
  const isAllSelected = selectedEmployees.length === allUserValues.length && allUserValues.length > 0;

  const allCourseValues = filteredCourses.map((course) => course.id);
  const isAllCoursesSelected = selectedCourses.length === allCourseValues.length && allCourseValues.length > 0;

  const allBundleValues = filteredBundles.map((bundle) => bundle.id);
  const isAllBundlesSelected = selectedBundles.length === allBundleValues.length && allBundleValues.length > 0;

  const handleSelectAll = (checked) => form.setFieldsValue({ employees: checked ? allUserValues : [] });
  const handleSelectAllCourses = (checked) => form.setFieldsValue({ courses: checked ? allCourseValues : [] });
  const handleSelectAllBundles = (checked) => form.setFieldsValue({ bundles: checked ? allBundleValues : [] });

  const handleOpenModal = () => {
    form.resetFields();
    form.setFieldsValue({ groupId, employees: [], courses: [], bundles: [] });
    fetchAddableUsers();
    fetchEnrolledItems();
  };


  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);
      const builder = new PayloadBuilder()
        .set("groupId", Number(values.groupId))
        .setIfExists("employees", values.employees)
        .setIfExists("courses", values.courses)
        .setIfExists("bundles", values.bundles)
        .setIfExists(
          "deadline",
          values.deadline ? values.deadline.format("YYYY-MM-DDTHH:mm:ss") : null
        )
        .set("assignedAt", dayjs().format("YYYY-MM-DDTHH:mm:ss"));

      const payload = builder.build();

      const result = await addUser(payload);

      if (result?.success) {
        showSuccessMessage("User added successfully!");
        getUsers();
        if (getCourses) getCourses();
        if (getBundles) getBundles();
        handleCloseModal();
      } else {
        showErrorMessage(result?.data || "Error occurred while adding user.");
      }
    } catch (error) {
      if (!error?.errorFields) {
        showErrorMessage(error?.message || "Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageLoader visible={isSubmitting} />

      <CustomModal
        title="Add New User"
        open={isModalOpen}
        onCancel={handleCloseModal}
        width={550}
        afterOpenChange={(open) => {
          if (open) handleOpenModal();
        }}
      >
        <Form form={form} layout="vertical" name="add_user_form" className="flex flex-col gap-2">
          <Form.Item name="groupId" noStyle>
            <Input type="hidden" />
          </Form.Item>

          <InputWrapper label="Select Employees" required>
            <Form.Item
              name="employees"
              rules={[{ required: true, message: "Please select at least one employee" }]}
            >
              <CustomLabelDropdown
                placeholder="Select Employees"
                showSearch
                mode="multiple"
                maxTagCount="responsive"
                notFoundContent={usersLoading ? <Spin size="small" /> : "No users available"}
                options={filteredUsers}
                dropdownRender={(menu) => (
                  <>
                    <div className="px-3 py-2 border-b border-gray-100 cursor-pointer">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={selectedEmployees.length > 0 && !isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      >
                        <strong>Select All</strong>
                      </Checkbox>
                    </div>
                    {menu}
                  </>
                )}
              />
            </Form.Item>
          </InputWrapper>
          <InputWrapper label="Select Courses">
            <Form.Item name="courses">
              <CustomLabelDropdown
                placeholder="Select Courses"
                showSearch
                mode="multiple"
                maxTagCount="responsive"
                notFoundContent={enrolledItemsLoading ? <Spin size="small" /> : "No courses available"}
                options={filteredCourses.map((c) => ({ value: c.id, label: c.name }))}
                dropdownRender={(menu) => (
                  <>
                    <div className="px-3 py-2 border-b border-gray-100 cursor-pointer">
                      <Checkbox
                        checked={isAllCoursesSelected}
                        indeterminate={selectedCourses.length > 0 && !isAllCoursesSelected}
                        onChange={(e) => handleSelectAllCourses(e.target.checked)}
                      >
                        <strong>Select All</strong>
                      </Checkbox>
                    </div>
                    {menu}
                  </>
                )}
              />
            </Form.Item>
          </InputWrapper>

          <InputWrapper label="Select Bundles">
            <Form.Item name="bundles">
              <CustomLabelDropdown
                placeholder="Select Bundles"
                showSearch
                mode="multiple"
                maxTagCount="responsive"
                notFoundContent={enrolledItemsLoading ? <Spin size="small" /> : "No bundles available"}
                options={filteredBundles.map((b) => ({ value: b.id, label: b.name }))}
                dropdownRender={(menu) => (
                  <>
                    <div className="px-3 py-2 border-b border-gray-100 cursor-pointer">
                      <Checkbox
                        checked={isAllBundlesSelected}
                        indeterminate={selectedBundles.length > 0 && !isAllBundlesSelected}
                        onChange={(e) => handleSelectAllBundles(e.target.checked)}
                      >
                        <strong>Select All</strong>
                      </Checkbox>
                    </div>
                    {menu}
                  </>
                )}
              />
            </Form.Item>
          </InputWrapper>
          {(selectedCourses?.length > 0 || selectedBundles?.length > 0) && (
            <InputWrapper label="Deadline" required>
              <Form.Item
                name="deadline"
                rules={[{ required: true, message: "Please select a deadline" }]}
              >
                <DatePicker
                  className="w-full h-[46px] rounded-[10px] px-4"
                  disabledDate={(current) => current && current < dayjs().endOf("day")}
                />
              </Form.Item>
            </InputWrapper>
          )}

          <div className="flex justify-center items-center gap-3 w-full mt-4 pb-2">
            <CustomButton
              className="w-1/2"
              variant="primary"
              onClick={handleAdd}
              loading={isSubmitting}
            >
              Add
            </CustomButton>
            <CustomButton
              className="w-1/2"
              variant="outlined"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancel
            </CustomButton>
          </div>
        </Form>
      </CustomModal>
    </>
  );
};

export default AddUserModal;